import { useCallback, useEffect, useRef, useState } from "react";
import firebaseService from "firebase_service/firebaseService";
import { SentryIssueSort, StatsPeriod } from "../../../api/sentryIssues";

export const ALL_DOMAINS = "__all__";

export type SentryStatusFilter = "unresolved" | "resolved" | "ignored" | "all";

export type SentryFilters = {
  /** Sentry project slugs; empty means every project. */
  projects: string[];
  status: SentryStatusFilter;
  statsPeriod: StatsPeriod;
  sort: SentryIssueSort;
  environment: string;
  search: string;
  domain: string;
};

export const DEFAULT_SENTRY_FILTERS: SentryFilters = {
  projects: [],
  status: "unresolved",
  statsPeriod: "24h",
  sort: SentryIssueSort.LastSeen,
  environment: "",
  search: "",
  domain: ALL_DOMAINS,
};

function getStorageKey(): string | null {
  const userId = firebaseService.getUserId();
  if (!userId) return null;
  return `sentry-issue-filters-${userId}`;
}

function readFromStorage(): SentryFilters {
  try {
    const key = getStorageKey();
    if (!key) return DEFAULT_SENTRY_FILTERS;
    const raw = localStorage.getItem(key);
    if (!raw) return DEFAULT_SENTRY_FILTERS;

    const parsed = JSON.parse(raw) as Partial<SentryFilters> | null;
    if (!parsed || typeof parsed !== "object") return DEFAULT_SENTRY_FILTERS;

    // Merge over the defaults so a stored shape from an older build (or a
    // hand-edited value) cannot leave a field undefined.
    return {
      ...DEFAULT_SENTRY_FILTERS,
      ...parsed,
      projects: Array.isArray(parsed.projects) ? parsed.projects : [],
    };
  } catch {
    return DEFAULT_SENTRY_FILTERS;
  }
}

function writeToStorage(filters: SentryFilters): void {
  try {
    const key = getStorageKey();
    if (!key) return;
    localStorage.setItem(key, JSON.stringify(filters));
  } catch {
    // Quota or private mode — in-memory state still works for the session.
  }
}

/**
 * Sentry issue filters, remembered per user across visits — the same treatment
 * the tickets board gives its filters, so returning to triage does not mean
 * re-picking the same project and window every time.
 */
export function useLocalSentryFilters() {
  const [filters, setFilters] = useState<SentryFilters>(readFromStorage);

  // Reload when the signed-in user changes without a remount.
  const userIdRef = useRef(firebaseService.getUserId());
  useEffect(() => {
    const currentUserId = firebaseService.getUserId();
    if (currentUserId !== userIdRef.current) {
      userIdRef.current = currentUserId;
      setFilters(readFromStorage());
    }
  });

  // Coalesce rapid changes (typing in the search box) into one write.
  const pendingWrite = useRef<SentryFilters | null>(null);
  const writeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleWrite = useCallback((next: SentryFilters) => {
    pendingWrite.current = next;
    if (writeTimer.current) clearTimeout(writeTimer.current);
    writeTimer.current = setTimeout(() => {
      if (pendingWrite.current) writeToStorage(pendingWrite.current);
      pendingWrite.current = null;
      writeTimer.current = null;
    }, 300);
  }, []);

  useEffect(
    () => () => {
      if (writeTimer.current) clearTimeout(writeTimer.current);
      if (pendingWrite.current) writeToStorage(pendingWrite.current);
    },
    [],
  );

  const setFilter = useCallback(
    <K extends keyof SentryFilters>(field: K, value: SentryFilters[K]) => {
      setFilters((previous) => {
        const next = { ...previous, [field]: value };
        scheduleWrite(next);
        return next;
      });
    },
    [scheduleWrite],
  );

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_SENTRY_FILTERS);
    scheduleWrite(DEFAULT_SENTRY_FILTERS);
  }, [scheduleWrite]);

  const isDefault =
    filters.projects.length === 0 &&
    filters.status === DEFAULT_SENTRY_FILTERS.status &&
    filters.statsPeriod === DEFAULT_SENTRY_FILTERS.statsPeriod &&
    filters.sort === DEFAULT_SENTRY_FILTERS.sort &&
    filters.environment === DEFAULT_SENTRY_FILTERS.environment &&
    filters.search === DEFAULT_SENTRY_FILTERS.search &&
    filters.domain === DEFAULT_SENTRY_FILTERS.domain;

  return { filters, setFilter, resetFilters, isDefault };
}
